import { useEffect, useState } from 'react';
import { CircleMarker, MapContainer, Polygon, Polyline, TileLayer, useMapEvents } from 'react-leaflet';
import AppLayout from '../../components/AppLayout';
import {
  getCompanyProfile,
  updateCompanyProfile,
  updatePulseSettings,
} from '../../services/companyService';
import { normalizeServiceCountyName } from '../../utils/serviceArea';
import { getCountiesForState } from '../../utils/usCounties';
import { searchMapboxPlaces } from '../../utils/mapbox';
import { buildRadiusPolygon } from '../../utils/pulseZones';

const defaultPulseSettings = {
  enabled: false,
  configVersion: 1,
  pickupThreshold: 5,
  detectionRadiusMiles: 0.5,
  timeWindowMinutes: 15,
  cooldownMinutes: 20,
  alertExpirationMinutes: 30,
  maxAlertsPerHour: 4,
  automaticDetection: true,
  notifyOnlineOnly: false,
  zones: [],
  useAlertLevels: true,
  alertLevelOverride: 'Warm',
  busyMultiplier: 1.6,
  hotMultiplier: 2.5,
  criticalMultiplier: 4.0,
};

const defaultMobileUpdateSettings = {
  enabled: true,
  android: {
    minimumVersion: '2.0.0',
    minimumBuildNumber: 62,
    storeUrl: 'https://play.google.com/store/apps/details?id=com.oldalexhub.taxiops.driver',
    message: 'A required TaxiOps Driver update is available. Update now to continue.',
  },
  ios: {
    minimumVersion: '2.0.10',
    minimumBuildNumber: 21,
    storeUrl: 'https://apps.apple.com/us/app/taxiops-driver/id6775650748',
    message: 'A required TaxiOps Driver update is available. Update now to continue.',
  },
};

const pulseZoneColor = (category) => ({
  Airport: '#38bdf8',
  Downtown: '#a78bfa',
  Custom: '#34d399',
}[category] || '#34d399');

const openPolygonRing = (coordinates) => {
  const ring = Array.isArray(coordinates?.[0]) ? coordinates[0] : [];
  if (ring.length > 1) {
    const first = ring[0];
    const last = ring[ring.length - 1];
    if (first?.[0] === last?.[0] && first?.[1] === last?.[1]) return ring.slice(0, -1);
  }
  return ring;
};

const normalizePulseZone = (zone = {}) => ({
  ...(zone._id ? { _id: zone._id } : {}),
  name: zone.name || '',
  category: zone.category || 'Custom',
  enabled: zone.enabled === true,
  boundaryMode: zone.boundaryMode === 'radius' ? 'radius' : 'manual',
  ...(zone.boundaryMode === 'radius' ? {
    centerLat: Number(zone.centerLat),
    centerLon: Number(zone.centerLon),
    radiusMiles: Number(zone.radiusMiles) || 1,
  } : {}),
  polygon: {
    type: 'Polygon',
    coordinates: [openPolygonRing(zone.polygon?.coordinates)],
  },
});

const PulseZoneMapEvents = ({ disabled, onMapClick }) => {
  useMapEvents({
    click(event) {
      if (!disabled) onMapClick([event.latlng.lng, event.latlng.lat]);
    },
  });
  return null;
};

const SETTINGS_SECTION_IDS = [
  'profile',
  'service-area',
  'dispatch',
  'hours-of-service',
  'diagnostics',
  'pulse',
  'stale-trip',
  'live-tracking',
  'mobile-updates',
  'safety-scoring',
  'safety-report',
];

const SettingsCard = ({ id, category, title, description, open, onToggle, children }) => {
  const contentId = `settings-card-${id}`;
  return (
    <section className={`settings-card${open ? ' is-open' : ''}`}>
      <button
        type="button"
        className="settings-card-toggle"
        aria-expanded={open}
        aria-controls={contentId}
        onClick={onToggle}
      >
        <span className="settings-card-heading">
          <span className="settings-card-category">{category}</span>
          <span className="settings-card-title">{title}</span>
          <span className="settings-card-description">{description}</span>
        </span>
        <span className="settings-card-icon" aria-hidden="true">{open ? '−' : '+'}</span>
      </button>
      {open ? <div id={contentId} className="settings-card-body panel-body">{children}</div> : null}
    </section>
  );
};

const defaultForm = {
  name: '',
  address: '',
  phone: '',
  email: '',
  website: '',
  logoUrl: '',
  notes: '',
  allowedStates: ['FL'],
  serviceAreas: {
    counties: [],
  },
  dispatchSettings: {
    maxDistanceMiles: 6,
    maxCandidates: 20,
    acceptTimeoutSeconds: 45,
    driverLocationIntervalSeconds: 45,
    distanceStepsMiles: '1,2,3,4,5,6',
  },
    hosSettings: {
      MAX_ON_DUTY_HOURS: 12,
      REQUIRED_OFF_DUTY_HOURS: 12,
      LOOKBACK_WINDOW_HOURS: 24,
      RECORD_RETENTION_MONTHS: 12,
      ALLOW_ALTERNATE_RULES: false,
      ALERT_THRESHOLD_HOURS: 11.5,
    },
    diagnosticsSettings: {
      retentionDays: 90,
    },
    pulseSettings: { ...defaultPulseSettings },
    staleTripSettings: {
      enabled: true,
      inactivityHours: 2,
      acknowledgementMinutes: 5,
      meaningfulMovementMeters: 100,
    },
    companyTimezone: 'America/New_York',
    trackingSettings: { movingIntervalSeconds: 5, stationaryIntervalSeconds: 30, maxAcceptedAccuracyMeters: 50, rawTelemetryRetentionDays: 1 },
    mobileUpdateSettings: { ...defaultMobileUpdateSettings },
    safetyScoringSettings: {
      enabled: true, preset: 'balanced', accelerationThresholdMps2: 3, brakingThresholdMps2: -3,
      corneringThresholdMps2: 3, speedingOverMph: 10, severeSpeedingOverMph: 20,
      speedingMinimumSeconds: 15, maxGpsAccuracyMeters: 50, eventCooldownSeconds: 10, minimumMileage: 25,
      eventRateBasisMiles: 100,
      weights: { acceleration: 1, braking: 1.5, cornering: 1.5, moderateSpeedingPerMinute: 0.5, severeSpeedingPerMinute: 1 },
    },
    safetyDigestSettings: { enabled: true, frequency: 'weekly', weekday: 1, dayOfMonth: 1, localTime: '07:00', timezone: 'America/New_York' },
};

const CompanySettings = () => {
  const [form, setForm] = useState(defaultForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [pulseSaving, setPulseSaving] = useState(false);
  const [pulseError, setPulseError] = useState('');
  const [pulseSuccess, setPulseSuccess] = useState('');
  const [activeZoneIndex, setActiveZoneIndex] = useState(0);
  const [zoneSearchQuery, setZoneSearchQuery] = useState('');
  const [zoneSearchResults, setZoneSearchResults] = useState([]);
  const [zoneSearchLoading, setZoneSearchLoading] = useState(false);
  const [zoneSearchError, setZoneSearchError] = useState('');
  const [countyOptions, setCountyOptions] = useState([]);
  const [countiesLoading, setCountiesLoading] = useState(false);
  const [countiesError, setCountiesError] = useState('');
  const [openSections, setOpenSections] = useState(() => new Set(['profile']));

  const toggleSection = (sectionId) => {
    setOpenSections((current) => {
      const next = new Set(current);
      if (next.has(sectionId)) next.delete(sectionId);
      else next.add(sectionId);
      return next;
    });
  };

  const setAllSections = (open) => {
    setOpenSections(open ? new Set(SETTINGS_SECTION_IDS) : new Set());
  };

  useEffect(() => {
    let ignore = false;

    const fetchProfile = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await getCompanyProfile();
        if (ignore) return;
        const data = res.data?.company;
        if (data && typeof data === 'object') {
          setForm({
            name: data.name || '',
            address: data.address || '',
            phone: data.phone || '',
            email: data.email || '',
            website: data.website || '',
            logoUrl: data.logoUrl || '',
            notes: data.notes || '',
            allowedStates: Array.isArray(data.allowedStates) && data.allowedStates.length > 0 ? data.allowedStates : defaultForm.allowedStates,
            serviceAreas: {
              counties: Array.isArray(data.serviceAreas?.counties)
                ? data.serviceAreas.counties.map(normalizeServiceCountyName).filter(Boolean)
                : [],
            },
            dispatchSettings: {
              maxDistanceMiles: data.dispatchSettings?.maxDistanceMiles ?? defaultForm.dispatchSettings.maxDistanceMiles,
              maxCandidates: data.dispatchSettings?.maxCandidates ?? defaultForm.dispatchSettings.maxCandidates,
              acceptTimeoutSeconds: data.dispatchSettings?.acceptTimeoutSeconds ?? defaultForm.dispatchSettings.acceptTimeoutSeconds,
              driverLocationIntervalSeconds: data.dispatchSettings?.driverLocationIntervalSeconds ?? defaultForm.dispatchSettings.driverLocationIntervalSeconds,
                distanceStepsMiles: Array.isArray(data.dispatchSettings?.distanceStepsMiles)
                  ? (data.dispatchSettings.distanceStepsMiles.join(',') )
                  : defaultForm.dispatchSettings.distanceStepsMiles,
            },
              hosSettings: {
                MAX_ON_DUTY_HOURS: data.hosSettings?.MAX_ON_DUTY_HOURS ?? defaultForm.hosSettings.MAX_ON_DUTY_HOURS,
                REQUIRED_OFF_DUTY_HOURS: data.hosSettings?.REQUIRED_OFF_DUTY_HOURS ?? defaultForm.hosSettings.REQUIRED_OFF_DUTY_HOURS,
                LOOKBACK_WINDOW_HOURS: data.hosSettings?.LOOKBACK_WINDOW_HOURS ?? defaultForm.hosSettings.LOOKBACK_WINDOW_HOURS,
                RECORD_RETENTION_MONTHS: data.hosSettings?.RECORD_RETENTION_MONTHS ?? defaultForm.hosSettings.RECORD_RETENTION_MONTHS,
                ALLOW_ALTERNATE_RULES: data.hosSettings?.ALLOW_ALTERNATE_RULES ?? defaultForm.hosSettings.ALLOW_ALTERNATE_RULES,
                ALERT_THRESHOLD_HOURS: data.hosSettings?.ALERT_THRESHOLD_HOURS ?? defaultForm.hosSettings.ALERT_THRESHOLD_HOURS,
              },
              diagnosticsSettings: {
                retentionDays: data.diagnosticsSettings?.retentionDays ?? defaultForm.diagnosticsSettings.retentionDays,
              },
              pulseSettings: {
                enabled: data.pulseSettings?.enabled ?? defaultPulseSettings.enabled,
                configVersion: data.pulseSettings?.configVersion ?? defaultPulseSettings.configVersion,
                pickupThreshold: data.pulseSettings?.pickupThreshold ?? defaultPulseSettings.pickupThreshold,
                detectionRadiusMiles: data.pulseSettings?.detectionRadiusMiles ?? defaultPulseSettings.detectionRadiusMiles,
                timeWindowMinutes: data.pulseSettings?.timeWindowMinutes ?? defaultPulseSettings.timeWindowMinutes,
                cooldownMinutes: data.pulseSettings?.cooldownMinutes ?? defaultPulseSettings.cooldownMinutes,
                alertExpirationMinutes: data.pulseSettings?.alertExpirationMinutes ?? defaultPulseSettings.alertExpirationMinutes,
                maxAlertsPerHour: data.pulseSettings?.maxAlertsPerHour ?? defaultPulseSettings.maxAlertsPerHour,
                automaticDetection: data.pulseSettings?.automaticDetection ?? defaultPulseSettings.automaticDetection,
                notifyOnlineOnly: data.pulseSettings?.notifyOnlineOnly ?? defaultPulseSettings.notifyOnlineOnly,
                zones: Array.isArray(data.pulseSettings?.zones)
                  ? data.pulseSettings.zones.map(normalizePulseZone)
                  : [],
                useAlertLevels: data.pulseSettings?.useAlertLevels ?? defaultPulseSettings.useAlertLevels,
                alertLevelOverride: data.pulseSettings?.alertLevelOverride ?? defaultPulseSettings.alertLevelOverride,
                busyMultiplier: data.pulseSettings?.busyMultiplier ?? defaultPulseSettings.busyMultiplier,
                hotMultiplier: data.pulseSettings?.hotMultiplier ?? defaultPulseSettings.hotMultiplier,
                criticalMultiplier: data.pulseSettings?.criticalMultiplier ?? defaultPulseSettings.criticalMultiplier,
              },
              staleTripSettings: {
                enabled: data.staleTripSettings?.enabled ?? true,
                inactivityHours: data.staleTripSettings?.inactivityHours ?? ((data.staleTripSettings?.timeoutMinutes ?? 120) / 60),
                acknowledgementMinutes: data.staleTripSettings?.acknowledgementMinutes ?? 5,
                meaningfulMovementMeters: data.staleTripSettings?.meaningfulMovementMeters ?? 100,
              },
              companyTimezone: data.companyTimezone || defaultForm.companyTimezone,
              trackingSettings: { ...defaultForm.trackingSettings, ...(data.trackingSettings || {}) },
              mobileUpdateSettings: {
                ...defaultMobileUpdateSettings,
                ...(data.mobileUpdateSettings || {}),
                android: { ...defaultMobileUpdateSettings.android, ...(data.mobileUpdateSettings?.android || {}) },
                ios: { ...defaultMobileUpdateSettings.ios, ...(data.mobileUpdateSettings?.ios || {}) },
              },
              safetyScoringSettings: { ...defaultForm.safetyScoringSettings, ...(data.safetyScoringSettings || {}), weights: { ...defaultForm.safetyScoringSettings.weights, ...(data.safetyScoringSettings?.weights || {}) } },
              safetyDigestSettings: { ...defaultForm.safetyDigestSettings, ...(data.safetyDigestSettings || {}) },
          });
        }
      } catch (err) {
        if (!ignore) {
          const message = err.response?.data?.message || 'Unable to load company profile.';
          setError(message);
        }
      } finally {
        if (!ignore) setLoading(false);
      }
    };

    fetchProfile();
    return () => {
      ignore = true;
    };
  }, []);

  const selectedServiceState = Array.isArray(form.allowedStates) ? form.allowedStates[0] || '' : '';
  const mergedCountyOptions = Array.from(
    new Set([...(countyOptions || []), ...((form.serviceAreas?.counties || []).map(normalizeServiceCountyName))]),
  )
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b));

  useEffect(() => {
    let ignore = false;

    const fetchCounties = async () => {
      setCountyOptions([]);
      setCountiesError('');
      if (!selectedServiceState) return;
      setCountiesLoading(true);
      try {
        const counties = await getCountiesForState(selectedServiceState);
        if (ignore) return;
        setCountyOptions(counties);
        if (!counties.length) {
          setCountiesError('County options are unavailable for this state right now.');
        }
      } catch (_err) {
        if (!ignore) setCountiesError('Unable to load county options right now.');
      } finally {
        if (!ignore) setCountiesLoading(false);
      }
    };

    fetchCounties();
    return () => {
      ignore = true;
    };
  }, [selectedServiceState]);

  useEffect(() => {
    setZoneSearchQuery('');
    setZoneSearchResults([]);
    setZoneSearchError('');
  }, [activeZoneIndex]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Full list of US states (codes and labels) for the multi-select.
  const STATE_OPTIONS = [
    { code: 'AL', label: 'Alabama' },{ code: 'AK', label: 'Alaska' },{ code: 'AZ', label: 'Arizona' },{ code: 'AR', label: 'Arkansas' },{ code: 'CA', label: 'California' },{ code: 'CO', label: 'Colorado' },{ code: 'CT', label: 'Connecticut' },{ code: 'DE', label: 'Delaware' },{ code: 'DC', label: 'District of Columbia' },{ code: 'FL', label: 'Florida' },{ code: 'GA', label: 'Georgia' },{ code: 'HI', label: 'Hawaii' },{ code: 'ID', label: 'Idaho' },{ code: 'IL', label: 'Illinois' },{ code: 'IN', label: 'Indiana' },{ code: 'IA', label: 'Iowa' },{ code: 'KS', label: 'Kansas' },{ code: 'KY', label: 'Kentucky' },{ code: 'LA', label: 'Louisiana' },{ code: 'ME', label: 'Maine' },{ code: 'MD', label: 'Maryland' },{ code: 'MA', label: 'Massachusetts' },{ code: 'MI', label: 'Michigan' },{ code: 'MN', label: 'Minnesota' },{ code: 'MS', label: 'Mississippi' },{ code: 'MO', label: 'Missouri' },{ code: 'MT', label: 'Montana' },{ code: 'NE', label: 'Nebraska' },{ code: 'NV', label: 'Nevada' },{ code: 'NH', label: 'New Hampshire' },{ code: 'NJ', label: 'New Jersey' },{ code: 'NM', label: 'New Mexico' },{ code: 'NY', label: 'New York' },{ code: 'NC', label: 'North Carolina' },{ code: 'ND', label: 'North Dakota' },{ code: 'OH', label: 'Ohio' },{ code: 'OK', label: 'Oklahoma' },{ code: 'OR', label: 'Oregon' },{ code: 'PA', label: 'Pennsylvania' },{ code: 'RI', label: 'Rhode Island' },{ code: 'SC', label: 'South Carolina' },{ code: 'SD', label: 'South Dakota' },{ code: 'TN', label: 'Tennessee' },{ code: 'TX', label: 'Texas' },{ code: 'UT', label: 'Utah' },{ code: 'VT', label: 'Vermont' },{ code: 'VA', label: 'Virginia' },{ code: 'WA', label: 'Washington' },{ code: 'WV', label: 'West Virginia' },{ code: 'WI', label: 'Wisconsin' },{ code: 'WY', label: 'Wyoming' }
  ];

  const handleAllowedStatesChange = (event) => {
    // single-select: store selected value as a one-element array to match server shape
    const value = event.target.value || '';
    setForm((prev) => ({ ...prev, allowedStates: value ? [value] : [], serviceAreas: { counties: [] } }));
  };

  const handleServiceCountiesChange = (event) => {
    const counties = Array.from(event.target.selectedOptions)
      .map((option) => normalizeServiceCountyName(option.value))
      .filter(Boolean);
    setForm((prev) => ({
      ...prev,
      serviceAreas: {
        ...prev.serviceAreas,
        counties,
      },
    }));
  };

  const updateZoneAt = (index, patch) => {
    setForm((prev) => {
      const zones = [...(prev.pulseSettings?.zones || [])];
      if (!zones[index]) return prev;
      zones[index] = { ...zones[index], ...patch };
      return {
        ...prev,
        pulseSettings: { ...prev.pulseSettings, zones },
      };
    });
  };

  const addPulseZone = () => {
    const nextIndex = form.pulseSettings?.zones?.length || 0;
    setForm((prev) => ({
      ...prev,
      pulseSettings: {
        ...prev.pulseSettings,
        zones: [
          ...(prev.pulseSettings?.zones || []),
          {
            name: `New zone ${nextIndex + 1}`,
            category: 'Custom',
            enabled: false,
            boundaryMode: 'radius',
            radiusMiles: 1,
            polygon: { type: 'Polygon', coordinates: [[]] },
          },
        ],
      },
    }));
    setActiveZoneIndex(nextIndex);
    setPulseError('');
    setPulseSuccess('');
  };

  const deletePulseZone = (index) => {
    setForm((prev) => ({
      ...prev,
      pulseSettings: {
        ...prev.pulseSettings,
        zones: (prev.pulseSettings?.zones || []).filter((_, zoneIndex) => zoneIndex !== index),
      },
    }));
    setActiveZoneIndex((current) => Math.max(0, Math.min(current, (form.pulseSettings?.zones?.length || 1) - 2)));
  };

  const setRadiusZoneCenter = (index, centerLon, centerLat, radiusOverride) => {
    const zone = form.pulseSettings?.zones?.[index];
    if (!zone) return;
    const radiusMiles = Number(radiusOverride ?? zone.radiusMiles) || 1;
    updateZoneAt(index, {
      boundaryMode: 'radius',
      centerLon: Number(centerLon),
      centerLat: Number(centerLat),
      radiusMiles,
      polygon: {
        type: 'Polygon',
        coordinates: [buildRadiusPolygon(centerLon, centerLat, radiusMiles)],
      },
    });
  };

  const handleZoneMapClick = (coordinate) => {
    const zone = form.pulseSettings?.zones?.[activeZoneIndex];
    if (!zone) return;
    if (zone.boundaryMode === 'radius') {
      setRadiusZoneCenter(activeZoneIndex, coordinate[0], coordinate[1]);
      return;
    }
    const ring = openPolygonRing(zone.polygon?.coordinates);
    updateZoneAt(activeZoneIndex, {
      polygon: { type: 'Polygon', coordinates: [[...ring, coordinate]] },
    });
  };

  const updateZoneRadius = (value) => {
    const radiusMiles = Number(value);
    const zone = form.pulseSettings?.zones?.[activeZoneIndex];
    if (!zone) return;
    if (Number.isFinite(zone.centerLon) && Number.isFinite(zone.centerLat) && Number.isFinite(radiusMiles)) {
      setRadiusZoneCenter(activeZoneIndex, zone.centerLon, zone.centerLat, radiusMiles);
    } else {
      updateZoneAt(activeZoneIndex, { radiusMiles: value });
    }
  };

  const searchForZoneCenter = async () => {
    if (!zoneSearchQuery.trim()) return;
    setZoneSearchLoading(true);
    setZoneSearchError('');
    setZoneSearchResults([]);
    try {
      const zone = form.pulseSettings?.zones?.[activeZoneIndex];
      const proximity = Number.isFinite(zone?.centerLon) && Number.isFinite(zone?.centerLat)
        ? [zone.centerLon, zone.centerLat]
        : [-104.9903, 39.7392];
      const results = await searchMapboxPlaces(zoneSearchQuery, { proximity });
      setZoneSearchResults(results);
      if (!results.length) {
        setZoneSearchError('No matching places found. You can click the map to set the center instead.');
      }
    } catch (err) {
      setZoneSearchError(err.message || 'Location search is unavailable. Click the map to set the center.');
    } finally {
      setZoneSearchLoading(false);
    }
  };

  const chooseZoneSearchResult = (result) => {
    const zone = form.pulseSettings?.zones?.[activeZoneIndex];
    if (!zone) return;
    setRadiusZoneCenter(activeZoneIndex, result.centerLon, result.centerLat);
    if (!zone.name || /^New zone \d+$/i.test(zone.name)) {
      updateZoneAt(activeZoneIndex, { name: result.label.split(',')[0] });
    }
    setZoneSearchQuery(result.label);
    setZoneSearchResults([]);
    setZoneSearchError('');
  };

  const buildPulsePayload = ({ includeEnabled = false } = {}) => {
    const settings = form.pulseSettings || {};
    const payload = {
      automaticDetection: Boolean(settings.automaticDetection),
      notifyOnlineOnly: Boolean(settings.notifyOnlineOnly),
      useAlertLevels: Boolean(settings.useAlertLevels),
      alertLevelOverride: settings.alertLevelOverride || 'Warm',
      zones: (settings.zones || []).map(normalizePulseZone),
    };
    if (includeEnabled) payload.enabled = Boolean(settings.enabled);
    [
      'pickupThreshold', 'detectionRadiusMiles', 'timeWindowMinutes',
      'cooldownMinutes', 'alertExpirationMinutes', 'maxAlertsPerHour',
      'busyMultiplier', 'hotMultiplier', 'criticalMultiplier',
    ].forEach((field) => {
      const value = Number(settings[field]);
      if (Number.isFinite(value)) payload[field] = value;
    });
    return payload;
  };

  const applySavedPulseSettings = (settings) => {
    if (!settings) return;
    setForm((prev) => ({
      ...prev,
      pulseSettings: {
        ...prev.pulseSettings,
        ...settings,
        zones: Array.isArray(settings.zones)
          ? settings.zones.map(normalizePulseZone)
          : prev.pulseSettings?.zones || [],
      },
    }));
  };

  const handlePulseToggle = async (enabled) => {
    const previous = Boolean(form.pulseSettings?.enabled);
    setForm((prev) => ({
      ...prev,
      pulseSettings: { ...prev.pulseSettings, enabled },
    }));
    setPulseSaving(true);
    setPulseError('');
    setPulseSuccess('');
    try {
      const response = await updatePulseSettings({ enabled });
      applySavedPulseSettings(response.data?.pulseSettings);
      const retired = Number(response.data?.retiredAlerts || 0);
      setPulseSuccess(enabled
        ? 'TaxiOps Pulse enabled.'
        : `TaxiOps Pulse disabled. ${retired} active signal${retired === 1 ? '' : 's'} retired.`);
    } catch (err) {
      setForm((prev) => ({
        ...prev,
        pulseSettings: { ...prev.pulseSettings, enabled: previous },
      }));
      setPulseError(err.response?.data?.message || 'Failed to update TaxiOps Pulse.');
    } finally {
      setPulseSaving(false);
    }
  };

  const handlePulseSave = async () => {
    setPulseSaving(true);
    setPulseError('');
    setPulseSuccess('');
    try {
      const response = await updatePulseSettings(buildPulsePayload());
      applySavedPulseSettings(response.data?.pulseSettings);
      setPulseSuccess('TaxiOps Pulse settings and verified zones saved.');
    } catch (err) {
      setPulseError(err.response?.data?.message || 'Failed to save TaxiOps Pulse settings.');
    } finally {
      setPulseSaving(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      // Prepare payload
      const payload = { ...form };
      delete payload.pulseSettings;
      payload.serviceAreas = {
        counties: (form.serviceAreas?.counties || [])
          .map(normalizeServiceCountyName)
          .filter(Boolean),
      };
      // Parse dispatch settings
      if (form.dispatchSettings) {
        const ds = {};
        const md = Number(form.dispatchSettings.maxDistanceMiles);
        if (Number.isFinite(md)) ds.maxDistanceMiles = md;
        const mc = Number(form.dispatchSettings.maxCandidates);
        if (Number.isFinite(mc)) ds.maxCandidates = mc;
        const acceptTimeoutSeconds = Number(form.dispatchSettings.acceptTimeoutSeconds);
        if (Number.isFinite(acceptTimeoutSeconds)) ds.acceptTimeoutSeconds = acceptTimeoutSeconds;
        const driverLocationIntervalSeconds = Number(form.dispatchSettings.driverLocationIntervalSeconds);
        if ([30, 45, 60].includes(driverLocationIntervalSeconds)) {
          ds.driverLocationIntervalSeconds = driverLocationIntervalSeconds;
        }
        const stepsRaw = String(form.dispatchSettings.distanceStepsMiles || '')
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
          .map(Number)
          .filter((n) => Number.isFinite(n) && n > 0);
        ds.distanceStepsMiles = stepsRaw;
        payload.dispatchSettings = ds;
        // Parse HOS settings
        if (form.hosSettings) {
          const h = {};
          const maxOn = Number(form.hosSettings.MAX_ON_DUTY_HOURS);
          if (Number.isFinite(maxOn)) h.MAX_ON_DUTY_HOURS = maxOn;
          const reqOff = Number(form.hosSettings.REQUIRED_OFF_DUTY_HOURS);
          if (Number.isFinite(reqOff)) h.REQUIRED_OFF_DUTY_HOURS = reqOff;
          const look = Number(form.hosSettings.LOOKBACK_WINDOW_HOURS);
          if (Number.isFinite(look)) h.LOOKBACK_WINDOW_HOURS = look;
          const retain = Number(form.hosSettings.RECORD_RETENTION_MONTHS);
          if (Number.isFinite(retain)) h.RECORD_RETENTION_MONTHS = retain;
          h.ALLOW_ALTERNATE_RULES = Boolean(form.hosSettings.ALLOW_ALTERNATE_RULES);
          const alertH = Number(form.hosSettings.ALERT_THRESHOLD_HOURS);
          if (Number.isFinite(alertH)) h.ALERT_THRESHOLD_HOURS = alertH;
          payload.hosSettings = h;
        }
        if (form.diagnosticsSettings) {
          const days = Number(form.diagnosticsSettings.retentionDays);
          if (Number.isFinite(days) && days > 0) {
            payload.diagnosticsSettings = { retentionDays: days };
          }
        }
      }

      // Stale-trip settings
      if (form.staleTripSettings) {
        const hours = Number(form.staleTripSettings.inactivityHours);
        const ackMins = Number(form.staleTripSettings.acknowledgementMinutes);
        const movement = Number(form.staleTripSettings.meaningfulMovementMeters);
        payload.staleTripSettings = {
          enabled: Boolean(form.staleTripSettings.enabled),
          inactivityHours: Number.isFinite(hours) && hours >= 0.25 ? hours : 2,
          acknowledgementMinutes: Number.isFinite(ackMins) && ackMins >= 1 ? ackMins : 5,
          meaningfulMovementMeters: Number.isFinite(movement) && movement >= 10 ? movement : 100,
        };
      }
      payload.companyTimezone = form.companyTimezone || 'America/New_York';
      payload.trackingSettings = Object.fromEntries(Object.entries(form.trackingSettings || {}).map(([key, value]) => [key, Number(value)]));
      payload.mobileUpdateSettings = {
        enabled: Boolean(form.mobileUpdateSettings?.enabled),
        ...Object.fromEntries(['android', 'ios'].map((platform) => [platform, {
          ...form.mobileUpdateSettings?.[platform],
          minimumBuildNumber: Number(form.mobileUpdateSettings?.[platform]?.minimumBuildNumber),
        }])),
      };
      payload.safetyScoringSettings = {
        ...form.safetyScoringSettings,
        enabled: Boolean(form.safetyScoringSettings?.enabled),
        weights: Object.fromEntries(Object.entries(form.safetyScoringSettings?.weights || {}).map(([key, value]) => [key, Number(value)])),
      };
      payload.safetyDigestSettings = {
        ...form.safetyDigestSettings,
        enabled: Boolean(form.safetyDigestSettings?.enabled),
        weekday: Number(form.safetyDigestSettings?.weekday),
        dayOfMonth: Number(form.safetyDigestSettings?.dayOfMonth),
      };

      await updateCompanyProfile(payload);
      setSuccess('Company profile updated successfully.');
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to update company profile.';
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  const pulseZones = form.pulseSettings?.zones || [];
  const activePulseZone = pulseZones[activeZoneIndex] || null;
  const activeRing = openPolygonRing(activePulseZone?.polygon?.coordinates);
  const firstZonePoint = activeRing[0]
    || pulseZones.map((zone) => openPolygonRing(zone.polygon?.coordinates)[0]).find(Boolean);
  const activeRadiusCenter = activePulseZone?.boundaryMode === 'radius' &&
    Number.isFinite(activePulseZone.centerLat) && Number.isFinite(activePulseZone.centerLon)
    ? [activePulseZone.centerLat, activePulseZone.centerLon]
    : null;
  const pulseMapCenter = activeRadiusCenter
    || (firstZonePoint ? [firstZonePoint[1], firstZonePoint[0]] : [39.7392, -104.9903]);

  return (
    <AppLayout
      title="Company settings"
      subtitle="Manage the details used on receipts, reports and outbound emails."
    >
      <div className="panel company-settings-panel" style={{ maxWidth: '1040px', margin: '0 auto' }}>
        {loading ? (
          <div className="skeleton" style={{ height: '360px', borderRadius: '18px' }} />
        ) : (
          <form onSubmit={handleSubmit} className="panel-form">
            <div className="panel-header" style={{ flexWrap: 'wrap', gap: '12px' }}>
              <div>
                <h3>Configuration</h3>
                <p className="panel-subtitle">
                  Open only the area you need. Changes across all sections are saved together.
                </p>
              </div>
              <div className="company-settings-actions">
                <span className="badge badge-info">
                  {form.name ? form.name : 'Brand name pending'}
                </span>
                <button type="button" className="btn btn-subtle btn-compact" onClick={() => setAllSections(true)}>
                  Expand all
                </button>
                <button type="button" className="btn btn-subtle btn-compact" onClick={() => setAllSections(false)}>
                  Collapse all
                </button>
              </div>
            </div>

            <div className="settings-card-list">
              <SettingsCard
                id="profile"
                category="General"
                title="Company profile"
                description="Brand, contact, receipt and report details."
                open={openSections.has('profile')}
                onToggle={() => toggleSection('profile')}
              >
              <div>
                <label htmlFor="name">Company name</label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  value={form.name}
                  onChange={handleChange}
                  required
                />
              </div>
              <div>
                <label htmlFor="address">Address</label>
                <textarea
                  id="address"
                  name="address"
                  rows={2}
                  value={form.address}
                  onChange={handleChange}
                />
              </div>
              <div className="form-grid">
                <div>
                  <label htmlFor="phone">Phone</label>
                  <input
                    id="phone"
                    name="phone"
                    type="text"
                    value={form.phone}
                    onChange={handleChange}
                  />
                </div>
                <div>
                  <label htmlFor="email">Billing email</label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div className="form-grid">
                <div>
                  <label htmlFor="website">Website</label>
                  <input
                    id="website"
                    name="website"
                    type="url"
                    value={form.website}
                    onChange={handleChange}
                  />
                </div>
                <div>
                  <label htmlFor="logoUrl">Logo URL</label>
                  <input
                    id="logoUrl"
                    name="logoUrl"
                    type="url"
                    value={form.logoUrl}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div>
                <label htmlFor="notes">Additional notes</label>
                <textarea
                  id="notes"
                  name="notes"
                  rows={3}
                  value={form.notes}
                  onChange={handleChange}
                  placeholder="Internal reminders or payment instructions."
                />
              </div>
              </SettingsCard>

              <SettingsCard
                id="service-area"
                category="Operations"
                title="Service area"
                description="Operating state, counties, maps and reporting footprint."
                open={openSections.has('service-area')}
                onToggle={() => toggleSection('service-area')}
              >
                <div style={{ marginBottom: '12px' }}>
                  <label htmlFor="allowedStates">Service state</label>
                  <select
                    id="allowedStates"
                    name="allowedStates"
                    value={Array.isArray(form.allowedStates) ? form.allowedStates[0] || '' : ''}
                    onChange={handleAllowedStatesChange}
                    style={{ width: '100%', marginTop: '8px' }}
                  >
                    <option value="">Select a state</option>
                    {STATE_OPTIONS.map((s) => (
                      <option key={s.code} value={s.code}>
                        {s.label} ({s.code})
                      </option>
                    ))}
                  </select>
                  <p className="hint">Select the single US state where this service will operate.</p>
                </div>

                <div style={{ marginBottom: '18px' }}>
                  <label htmlFor="serviceCounties">Service area counties</label>
                  <select
                    id="serviceCounties"
                    multiple
                    size={Math.min(Math.max(mergedCountyOptions.length, 5), 10)}
                    value={form.serviceAreas?.counties || []}
                    onChange={handleServiceCountiesChange}
                    disabled={!selectedServiceState || countiesLoading || mergedCountyOptions.length === 0}
                    style={{ width: '100%', marginTop: '8px' }}
                  >
                    {mergedCountyOptions.map((county) => (
                      <option key={county} value={county}>
                        {county} County
                      </option>
                    ))}
                  </select>
                  <p className="hint">
                    {countiesLoading
                      ? 'Loading counties for the selected state.'
                      : 'Select all counties served inside the selected state. Maps and AI reports use this footprint.'}
                  </p>
                  {countiesError ? <p className="hint">{countiesError}</p> : null}
                  {form.serviceAreas?.counties?.length ? (
                    <p className="hint">
                      Selected: {form.serviceAreas.counties.map((county) => `${county} County`).join(', ')}
                    </p>
                  ) : (
                    <p className="hint">No counties selected yet, so maps will fall back to the center of the service state.</p>
                  )}
                </div>

              </SettingsCard>

              <SettingsCard
                id="dispatch"
                category="Operations"
                title="Dispatch settings"
                description="Candidate search radius, offer timing and location refresh."
                open={openSections.has('dispatch')}
                onToggle={() => toggleSection('dispatch')}
              >
                <div className="form-grid">
                  <div>
                    <label htmlFor="maxDistanceMiles">Max distance (miles)</label>
                    <input
                      id="maxDistanceMiles"
                      type="number"
                      step="0.1"
                      min="0"
                      value={form.dispatchSettings?.maxDistanceMiles ?? ''}
                      onChange={(e) => setForm((p) => ({ ...p, dispatchSettings: { ...p.dispatchSettings, maxDistanceMiles: e.target.value } }))}
                    />
                  </div>
                  <div>
                    <label htmlFor="maxCandidates">Max candidates</label>
                    <input
                      id="maxCandidates"
                      type="number"
                      min="1"
                      value={form.dispatchSettings?.maxCandidates ?? ''}
                      onChange={(e) => setForm((p) => ({ ...p, dispatchSettings: { ...p.dispatchSettings, maxCandidates: e.target.value } }))}
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="acceptTimeoutSeconds">Driver accept timeout (seconds)</label>
                  <input
                    id="acceptTimeoutSeconds"
                    type="number"
                    step="1"
                    min="5"
                    max="600"
                    value={form.dispatchSettings?.acceptTimeoutSeconds ?? ''}
                    onChange={(e) => setForm((p) => ({ ...p, dispatchSettings: { ...p.dispatchSettings, acceptTimeoutSeconds: e.target.value } }))}
                  />
                  <p className="hint">How long an automatic trip offer stays with one driver before moving to the next eligible nearest driver.</p>
                </div>
                <div>
                  <label htmlFor="driverLocationIntervalSeconds">Driver location update interval</label>
                  <select
                    id="driverLocationIntervalSeconds"
                    value={form.dispatchSettings?.driverLocationIntervalSeconds ?? 45}
                    onChange={(e) => setForm((p) => ({ ...p, dispatchSettings: { ...p.dispatchSettings, driverLocationIntervalSeconds: Number(e.target.value) } }))}
                    style={{ width: '100%', marginTop: '8px' }}
                  >
                    <option value={30}>30 seconds</option>
                    <option value={45}>45 seconds</option>
                    <option value={60}>60 seconds</option>
                  </select>
                  <p className="hint">Controls normal online driver map updates. Active trips continue using faster high-accuracy tracking.</p>
                </div>
                <div>
                  <label htmlFor="distanceStepsMiles">Distance steps (miles)</label>
                  <input
                    id="distanceStepsMiles"
                    type="text"
                    value={form.dispatchSettings?.distanceStepsMiles ?? ''}
                    onChange={(e) => setForm((p) => ({ ...p, dispatchSettings: { ...p.dispatchSettings, distanceStepsMiles: e.target.value } }))}
                    placeholder="Comma-separated e.g. 1,2,3,4"
                  />
                  <p className="hint">Comma-separated list of radial search distances in miles. Values will be sanitized.</p>
                </div>
                
              </SettingsCard>

              <SettingsCard
                id="hours-of-service"
                category="Compliance"
                title="Hours-of-Service (HOS)"
                description="Duty limits, rest requirements, alerts and record retention."
                open={openSections.has('hours-of-service')}
                onToggle={() => toggleSection('hours-of-service')}
              >
                <div className="form-grid">
                  <div>
                    <label htmlFor="MAX_ON_DUTY_HOURS">Max on-duty hours</label>
                    <input
                      id="MAX_ON_DUTY_HOURS"
                      type="number"
                      step="0.1"
                      min="0"
                      value={form.hosSettings?.MAX_ON_DUTY_HOURS ?? ''}
                      onChange={(e) => setForm((p) => ({ ...p, hosSettings: { ...p.hosSettings, MAX_ON_DUTY_HOURS: e.target.value } }))}
                    />
                  </div>
                  <div>
                    <label htmlFor="REQUIRED_OFF_DUTY_HOURS">Required off-duty hours</label>
                    <input
                      id="REQUIRED_OFF_DUTY_HOURS"
                      type="number"
                      step="0.1"
                      min="0"
                      value={form.hosSettings?.REQUIRED_OFF_DUTY_HOURS ?? ''}
                      onChange={(e) => setForm((p) => ({ ...p, hosSettings: { ...p.hosSettings, REQUIRED_OFF_DUTY_HOURS: e.target.value } }))}
                    />
                  </div>
                </div>
                <div className="form-grid">
                  <div>
                    <label htmlFor="LOOKBACK_WINDOW_HOURS">Lookback window (hours)</label>
                    <input
                      id="LOOKBACK_WINDOW_HOURS"
                      type="number"
                      step="1"
                      min="1"
                      value={form.hosSettings?.LOOKBACK_WINDOW_HOURS ?? ''}
                      onChange={(e) => setForm((p) => ({ ...p, hosSettings: { ...p.hosSettings, LOOKBACK_WINDOW_HOURS: e.target.value } }))}
                    />
                  </div>
                  <div>
                    <label htmlFor="RECORD_RETENTION_MONTHS">Record retention (months)</label>
                    <input
                      id="RECORD_RETENTION_MONTHS"
                      type="number"
                      step="1"
                      min="1"
                      value={form.hosSettings?.RECORD_RETENTION_MONTHS ?? ''}
                      onChange={(e) => setForm((p) => ({ ...p, hosSettings: { ...p.hosSettings, RECORD_RETENTION_MONTHS: e.target.value } }))}
                    />
                  </div>
                </div>
                <div className="form-grid">
                  <div>
                    <label htmlFor="ALLOW_ALTERNATE_RULES">Allow alternate rules</label>
                    <div style={{ marginTop: '8px' }}>
                      <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                        <input
                          id="ALLOW_ALTERNATE_RULES"
                          type="checkbox"
                          checked={Boolean(form.hosSettings?.ALLOW_ALTERNATE_RULES)}
                          onChange={(e) => setForm((p) => ({ ...p, hosSettings: { ...p.hosSettings, ALLOW_ALTERNATE_RULES: e.target.checked } }))}
                        />
                        <span className="hint">Enable only if taxicab rules allow the 15/10/70 alternate cycle.</span>
                      </label>
                    </div>
                  </div>
                  <div>
                    <label htmlFor="ALERT_THRESHOLD_HOURS">Alert threshold (hours)</label>
                    <input
                      id="ALERT_THRESHOLD_HOURS"
                      type="number"
                      step="0.1"
                      min="0"
                      value={form.hosSettings?.ALERT_THRESHOLD_HOURS ?? ''}
                      onChange={(e) => setForm((p) => ({ ...p, hosSettings: { ...p.hosSettings, ALERT_THRESHOLD_HOURS: e.target.value } }))}
                    />
                  </div>
                </div>
              </SettingsCard>

              <SettingsCard
                id="diagnostics"
                category="Data"
                title="Diagnostics"
                description="Retention for driver-app warning and error logs."
                open={openSections.has('diagnostics')}
                onToggle={() => toggleSection('diagnostics')}
              >
                <div className="form-grid">
                  <div>
                    <label htmlFor="diagnosticsRetentionDays">Retention period (days)</label>
                    <input
                      id="diagnosticsRetentionDays"
                      type="number"
                      step="1"
                      min="1"
                      max="365"
                      value={form.diagnosticsSettings?.retentionDays ?? ''}
                      onChange={(e) => setForm((p) => ({ ...p, diagnosticsSettings: { ...p.diagnosticsSettings, retentionDays: e.target.value } }))}
                    />
                    <p className="hint">Diagnostic records older than this many days are automatically removed by MongoDB. Takes effect immediately on save.</p>
                  </div>
                </div>
              </SettingsCard>

                {/* ── TaxiOps Pulse ─────────────────────────────────────────── */}
              <SettingsCard
                id="pulse"
                category="Intelligence"
                title="TaxiOps Pulse"
                description="Demand detection, driver alerts and geographic zones."
                open={openSections.has('pulse')}
                onToggle={() => toggleSection('pulse')}
              >

                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                    <input
                      type="checkbox"
                      checked={Boolean(form.pulseSettings?.enabled)}
                      onChange={(e) => handlePulseToggle(e.target.checked)}
                      disabled={pulseSaving}
                    />
                    <span><strong>Enable TaxiOps Pulse</strong></span>
                  </label>
                  <p className="hint" style={{ marginTop: 4 }}>
                    This control saves immediately. Disabling stops new delivery and retires active signals; enabling requires at least one saved, enabled zone.
                  </p>
                </div>

                {pulseError && <div className="feedback error">{pulseError}</div>}
                {pulseSuccess && <div className="feedback success">{pulseSuccess}</div>}

                <div className="form-grid">
                  <div>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                      <input
                        type="checkbox"
                        checked={Boolean(form.pulseSettings?.automaticDetection)}
                        onChange={(e) => setForm((p) => ({ ...p, pulseSettings: { ...p.pulseSettings, automaticDetection: e.target.checked } }))}
                      />
                      <span>Automatic demand detection</span>
                    </label>
                    <p className="hint">Run detection automatically on the configured interval.</p>
                  </div>
                  <div>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                      <input
                        type="checkbox"
                        checked={Boolean(form.pulseSettings?.notifyOnlineOnly)}
                        onChange={(e) => setForm((p) => ({ ...p, pulseSettings: { ...p.pulseSettings, notifyOnlineOnly: e.target.checked } }))}
                      />
                      <span>Notify online drivers only</span>
                    </label>
                    <p className="hint">When checked, only drivers currently Active + Online in the roster receive the alert. Unchecked sends to all drivers with registered devices.</p>
                  </div>
                </div>

                <div className="form-grid">
                  <div>
                    <label htmlFor="pulsePickupThreshold">Pickup threshold</label>
                    <input
                      id="pulsePickupThreshold"
                      type="number"
                      step="1"
                      min="2"
                      value={form.pulseSettings?.pickupThreshold ?? ''}
                      onChange={(e) => setForm((p) => ({ ...p, pulseSettings: { ...p.pulseSettings, pickupThreshold: e.target.value } }))}
                    />
                    <p className="hint">Minimum number of pickups in the radius to trigger an alert.</p>
                  </div>
                  <div>
                    <label htmlFor="pulseRadius">Detection radius (miles)</label>
                    <input
                      id="pulseRadius"
                      type="number"
                      step="0.1"
                      min="0.1"
                      value={form.pulseSettings?.detectionRadiusMiles ?? ''}
                      onChange={(e) => setForm((p) => ({ ...p, pulseSettings: { ...p.pulseSettings, detectionRadiusMiles: e.target.value } }))}
                    />
                    <p className="hint">Geographic radius used to cluster pickups for demand detection.</p>
                  </div>
                </div>

                <div className="form-grid">
                  <div>
                    <label htmlFor="pulseTimeWindow">Time window (minutes)</label>
                    <input
                      id="pulseTimeWindow"
                      type="number"
                      step="1"
                      min="1"
                      value={form.pulseSettings?.timeWindowMinutes ?? ''}
                      onChange={(e) => setForm((p) => ({ ...p, pulseSettings: { ...p.pulseSettings, timeWindowMinutes: e.target.value } }))}
                    />
                    <p className="hint">How far back to look for pickup activity when evaluating demand.</p>
                  </div>
                  <div>
                    <label htmlFor="pulseCooldown">Alert cooldown (minutes)</label>
                    <input
                      id="pulseCooldown"
                      type="number"
                      step="1"
                      min="1"
                      value={form.pulseSettings?.cooldownMinutes ?? ''}
                      onChange={(e) => setForm((p) => ({ ...p, pulseSettings: { ...p.pulseSettings, cooldownMinutes: e.target.value } }))}
                    />
                    <p className="hint">Minimum time before another alert can fire for the same geographic area.</p>
                  </div>
                </div>

                <div className="form-grid">
                  <div>
                    <label htmlFor="pulseExpiration">Alert expiration (minutes)</label>
                    <input
                      id="pulseExpiration"
                      type="number"
                      step="1"
                      min="1"
                      value={form.pulseSettings?.alertExpirationMinutes ?? ''}
                      onChange={(e) => setForm((p) => ({ ...p, pulseSettings: { ...p.pulseSettings, alertExpirationMinutes: e.target.value } }))}
                    />
                    <p className="hint">How long after an alert fires before it is considered expired.</p>
                  </div>
                  <div>
                    <label htmlFor="pulseMaxPerHour">Max alerts per hour</label>
                    <input
                      id="pulseMaxPerHour"
                      type="number"
                      step="1"
                      min="1"
                      value={form.pulseSettings?.maxAlertsPerHour ?? ''}
                      onChange={(e) => setForm((p) => ({ ...p, pulseSettings: { ...p.pulseSettings, maxAlertsPerHour: e.target.value } }))}
                    />
                    <p className="hint">Hard cap on the total number of Pulse alerts generated per 60-minute window.</p>
                  </div>
                </div>

                <div style={{ marginBottom: 16, marginTop: 12 }}>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                    <input
                      type="checkbox"
                      checked={Boolean(form.pulseSettings?.useAlertLevels)}
                      onChange={(e) => setForm((p) => ({ ...p, pulseSettings: { ...p.pulseSettings, useAlertLevels: e.target.checked } }))}
                    />
                    <span>Escalate alert level based on pickup count</span>
                  </label>
                  <p className="hint" style={{ marginTop: 4 }}>
                    Uncheck this if fares or operations are regulated and all demand alerts should fire at a single fixed level regardless of how many pickups are detected.
                  </p>
                </div>

                {!form.pulseSettings?.useAlertLevels && (
                  <div style={{ marginBottom: 16 }}>
                    <label htmlFor="pulseAlertLevelOverride">Fixed alert level</label>
                    <select
                      id="pulseAlertLevelOverride"
                      value={form.pulseSettings?.alertLevelOverride ?? 'Warm'}
                      onChange={(e) => setForm((p) => ({ ...p, pulseSettings: { ...p.pulseSettings, alertLevelOverride: e.target.value } }))}
                      style={{ width: '100%', marginTop: 8 }}
                    >
                      <option value="Warm">Warm</option>
                      <option value="Busy">Busy</option>
                      <option value="Hot">Hot</option>
                      <option value="Critical">Critical</option>
                    </select>
                    <p className="hint">Every alert will be classified at this level.</p>
                  </div>
                )}

                {form.pulseSettings?.useAlertLevels && (
                  <>
                    <p className="panel-subtitle">Alert level thresholds — expressed as multiples of the pickup threshold.</p>
                    <div className="form-grid">
                      <div>
                        <label htmlFor="pulseBusyMult">Busy multiplier</label>
                        <input
                          id="pulseBusyMult"
                          type="number"
                          step="0.1"
                          min="0.1"
                          value={form.pulseSettings?.busyMultiplier ?? ''}
                          onChange={(e) => setForm((p) => ({ ...p, pulseSettings: { ...p.pulseSettings, busyMultiplier: e.target.value } }))}
                        />
                        <p className="hint">Pickups ≥ threshold × this value → Busy level.</p>
                      </div>
                      <div>
                        <label htmlFor="pulseHotMult">Hot multiplier</label>
                        <input
                          id="pulseHotMult"
                          type="number"
                          step="0.1"
                          min="0.1"
                          value={form.pulseSettings?.hotMultiplier ?? ''}
                          onChange={(e) => setForm((p) => ({ ...p, pulseSettings: { ...p.pulseSettings, hotMultiplier: e.target.value } }))}
                        />
                        <p className="hint">Pickups ≥ threshold × this value → Hot level.</p>
                      </div>
                    </div>
                    <div className="form-grid">
                      <div>
                        <label htmlFor="pulseCritMult">Critical multiplier</label>
                        <input
                          id="pulseCritMult"
                          type="number"
                          step="0.1"
                          min="0.1"
                          value={form.pulseSettings?.criticalMultiplier ?? ''}
                          onChange={(e) => setForm((p) => ({ ...p, pulseSettings: { ...p.pulseSettings, criticalMultiplier: e.target.value } }))}
                        />
                        <p className="hint">Pickups ≥ threshold × this value → Critical level.</p>
                      </div>
                    </div>
                  </>
                )}

                <div style={{ borderTop: '1px solid var(--border-color)', marginTop: 24, paddingTop: 20 }}>
                  <div className="panel-header" style={{ alignItems: 'flex-start', gap: 12 }}>
                    <div>
                      <h4 style={{ margin: 0 }}>Verified Pulse zones</h4>
                      <p className="panel-subtitle" style={{ marginTop: 6 }}>
                        Search for a place and choose a radius, or click the map to set the center. TaxiOps creates the verified boundary automatically.
                      </p>
                    </div>
                    <button type="button" className="btn btn-subtle" onClick={addPulseZone} disabled={pulseSaving}>
                      Add zone
                    </button>
                  </div>

                  {pulseZones.length === 0 ? (
                    <div className="empty-state" style={{ marginBottom: 16 }}>
                      No verified zones yet. Add a zone, find its location, and choose its radius before enabling Pulse.
                    </div>
                  ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16, marginBottom: 16 }}>
                      <div style={{ display: 'grid', gap: 8, alignContent: 'start' }}>
                        {pulseZones.map((zone, index) => {
                          const pointCount = openPolygonRing(zone.polygon?.coordinates).length;
                          return (
                            <button
                              type="button"
                              key={zone._id || `pulse-zone-${index}`}
                              className={`btn ${activeZoneIndex === index ? 'btn-primary' : 'btn-subtle'}`}
                              onClick={() => setActiveZoneIndex(index)}
                              style={{ justifyContent: 'space-between', textAlign: 'left' }}
                            >
                              <span>{zone.name || `Zone ${index + 1}`}</span>
                              <span>{zone.enabled ? 'Enabled' : zone.boundaryMode === 'radius' ? `${zone.radiusMiles || 1} mi` : `${pointCount} points`}</span>
                            </button>
                          );
                        })}
                      </div>

                      {activePulseZone && (
                        <div style={{ display: 'grid', gap: 12 }}>
                          <div>
                            <label htmlFor="pulseZoneName">Zone name</label>
                            <input
                              id="pulseZoneName"
                              type="text"
                              maxLength="100"
                              value={activePulseZone.name}
                              onChange={(event) => updateZoneAt(activeZoneIndex, { name: event.target.value })}
                            />
                          </div>
                          <div className="form-grid">
                            <div>
                              <label htmlFor="pulseZoneCategory">Category</label>
                              <select
                                id="pulseZoneCategory"
                                value={activePulseZone.category}
                                onChange={(event) => updateZoneAt(activeZoneIndex, { category: event.target.value })}
                              >
                                <option value="Airport">Airport</option>
                                <option value="Downtown">Downtown</option>
                                <option value="Custom">Custom</option>
                              </select>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: 10 }}>
                              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                                <input
                                  type="checkbox"
                                  checked={activePulseZone.enabled}
                                  onChange={(event) => updateZoneAt(activeZoneIndex, { enabled: event.target.checked })}
                                />
                                <span>Enable this zone</span>
                              </label>
                            </div>
                          </div>

                          <div>
                            <label>Boundary setup</label>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                              <button
                                type="button"
                                className={`btn ${activePulseZone.boundaryMode === 'radius' ? 'btn-primary' : 'btn-subtle'}`}
                                onClick={() => {
                                  if (activePulseZone.boundaryMode !== 'radius') {
                                    updateZoneAt(activeZoneIndex, {
                                      boundaryMode: 'radius',
                                      centerLat: undefined,
                                      centerLon: undefined,
                                      radiusMiles: 1,
                                      polygon: { type: 'Polygon', coordinates: [[]] },
                                    });
                                  }
                                }}
                              >
                                Search or map center
                              </button>
                              <button
                                type="button"
                                className={`btn ${activePulseZone.boundaryMode === 'manual' ? 'btn-primary' : 'btn-subtle'}`}
                                onClick={() => {
                                  if (activePulseZone.boundaryMode !== 'manual') {
                                    updateZoneAt(activeZoneIndex, {
                                      boundaryMode: 'manual',
                                      centerLat: undefined,
                                      centerLon: undefined,
                                      radiusMiles: undefined,
                                      polygon: { type: 'Polygon', coordinates: [[]] },
                                    });
                                  }
                                }}
                              >
                                Draw manually (advanced)
                              </button>
                            </div>
                          </div>

                          {activePulseZone.boundaryMode === 'radius' ? (
                            <div style={{ display: 'grid', gap: 12 }}>
                              <div>
                                <label htmlFor="pulseZoneSearch">Find airport, downtown, or another place</label>
                                <div style={{ display: 'flex', gap: 8 }}>
                                  <input
                                    id="pulseZoneSearch"
                                    type="search"
                                    value={zoneSearchQuery}
                                    placeholder="e.g. Denver International Airport"
                                    onChange={(event) => setZoneSearchQuery(event.target.value)}
                                    onKeyDown={(event) => {
                                      if (event.key === 'Enter') {
                                        event.preventDefault();
                                        searchForZoneCenter();
                                      }
                                    }}
                                  />
                                  <button
                                    type="button"
                                    className="btn btn-subtle"
                                    onClick={searchForZoneCenter}
                                    disabled={zoneSearchLoading || !zoneSearchQuery.trim()}
                                  >
                                    {zoneSearchLoading ? 'Finding...' : 'Find'}
                                  </button>
                                </div>
                                {zoneSearchError && <p className="hint" style={{ color: 'var(--danger)' }}>{zoneSearchError}</p>}
                                {zoneSearchResults.length > 0 && (
                                  <div style={{ display: 'grid', gap: 6, marginTop: 8 }}>
                                    {zoneSearchResults.map((result) => (
                                      <button
                                        type="button"
                                        className="btn btn-subtle"
                                        key={result.id || result.label}
                                        onClick={() => chooseZoneSearchResult(result)}
                                        style={{ justifyContent: 'flex-start', textAlign: 'left' }}
                                      >
                                        {result.label}
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>
                              <div>
                                <label htmlFor="pulseZoneRadius">Zone radius (miles)</label>
                                <input
                                  id="pulseZoneRadius"
                                  type="number"
                                  min="0.05"
                                  max="50"
                                  step="0.1"
                                  value={activePulseZone.radiusMiles ?? 1}
                                  onChange={(event) => updateZoneRadius(event.target.value)}
                                />
                                <p className="hint">
                                  Start around 2 miles for an airport pickup area or 1 mile for a downtown core, then adjust while watching the map.
                                </p>
                                {Number.isFinite(activePulseZone.centerLat) && Number.isFinite(activePulseZone.centerLon) ? (
                                  <p className="hint">
                                    Center: {activePulseZone.centerLat.toFixed(5)}, {activePulseZone.centerLon.toFixed(5)}. Click elsewhere on the map to move it.
                                  </p>
                                ) : (
                                  <p className="hint">Search above or click the map once to set the center.</p>
                                )}
                              </div>
                            </div>
                          ) : (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                              <button
                                type="button"
                                className="btn btn-subtle"
                                disabled={activeRing.length === 0}
                                onClick={() => updateZoneAt(activeZoneIndex, {
                                  polygon: { type: 'Polygon', coordinates: [activeRing.slice(0, -1)] },
                                })}
                              >
                                Undo point
                              </button>
                              <button
                                type="button"
                                className="btn btn-subtle"
                                disabled={activeRing.length === 0}
                                onClick={() => updateZoneAt(activeZoneIndex, {
                                  polygon: { type: 'Polygon', coordinates: [[]] },
                                })}
                              >
                                Clear boundary
                              </button>
                            </div>
                          )}
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                            <button
                              type="button"
                              className="btn btn-danger"
                              onClick={() => deletePulseZone(activeZoneIndex)}
                            >
                              Delete zone
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <div style={{ height: 380, borderRadius: 14, overflow: 'hidden', border: '1px solid var(--border-color)' }}>
                    <MapContainer
                      key={`${pulseMapCenter[0]}-${pulseMapCenter[1]}-${pulseZones.length}`}
                      center={pulseMapCenter}
                      zoom={activeRadiusCenter || firstZonePoint ? 12 : 10}
                      style={{ width: '100%', height: '100%' }}
                    >
                      <TileLayer
                        attribution="&copy; OpenStreetMap contributors"
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        maxZoom={20}
                      />
                      {pulseZones.map((zone, index) => {
                        const ring = openPolygonRing(zone.polygon?.coordinates);
                        if (ring.length < 3) return null;
                        return (
                          <Polygon
                            key={zone._id || `zone-shape-${index}`}
                            positions={ring.map(([lon, lat]) => [lat, lon])}
                            pathOptions={{
                              color: pulseZoneColor(zone.category),
                              fillOpacity: activeZoneIndex === index ? 0.3 : 0.14,
                              weight: activeZoneIndex === index ? 4 : 2,
                            }}
                          />
                        );
                      })}
                      {activeRing.length > 1 && activeRing.length < 3 && (
                        <Polyline
                          positions={activeRing.map(([lon, lat]) => [lat, lon])}
                          pathOptions={{ color: pulseZoneColor(activePulseZone?.category), weight: 4 }}
                        />
                      )}
                      {activePulseZone?.boundaryMode === 'radius' &&
                        Number.isFinite(activePulseZone.centerLat) &&
                        Number.isFinite(activePulseZone.centerLon) && (
                          <CircleMarker
                            center={[activePulseZone.centerLat, activePulseZone.centerLon]}
                            radius={7}
                            pathOptions={{ color: pulseZoneColor(activePulseZone.category), fillOpacity: 1 }}
                          />
                        )}
                      <PulseZoneMapEvents disabled={!activePulseZone || pulseSaving} onMapClick={handleZoneMapClick} />
                    </MapContainer>
                  </div>
                  <p className="hint" style={{ marginTop: 8 }}>
                    In the easy mode, one search result or one map click creates the entire boundary. Enabled zones may not overlap. Driver alerts use only a zone, neighborhood, locality, or city label.
                  </p>

                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
                    <button type="button" className="btn btn-primary" onClick={handlePulseSave} disabled={pulseSaving}>
                      {pulseSaving ? 'Saving Pulse...' : 'Save Pulse settings'}
                    </button>
                  </div>
                </div>
              </SettingsCard>

            {/* ── Stale Trip Auto-Cancel ────────────────────────────────── */}
              <SettingsCard
                id="stale-trip"
                category="Safety"
                title="Stale trip auto-cancel"
                description="Inactivity warning, acknowledgement and cancellation thresholds."
                open={openSections.has('stale-trip')}
                onToggle={() => toggleSection('stale-trip')}
              >

              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                  <input
                    type="checkbox"
                    checked={Boolean(form.staleTripSettings?.enabled)}
                    onChange={(e) => setForm((p) => ({ ...p, staleTripSettings: { ...p.staleTripSettings, enabled: e.target.checked } }))}
                  />
                  <span><strong>Enable stale trip auto-cancel</strong></span>
                </label>
                <p className="hint" style={{ marginTop: 4 }}>
                  When disabled, no warnings or automatic cancellations occur.
                </p>
              </div>

              <div className="form-grid">
                <div>
                  <label htmlFor="staleTripTimeout">Inactivity timeout (hours)</label>
                  <input
                    id="staleTripTimeout"
                    type="number"
                    step="0.25"
                    min="0.25"
                    max="72"
                    value={form.staleTripSettings?.inactivityHours ?? ''}
                    onChange={(e) => setForm((p) => ({ ...p, staleTripSettings: { ...p.staleTripSettings, inactivityHours: e.target.value } }))}
                    disabled={!form.staleTripSettings?.enabled}
                  />
                  <p className="hint">
                    Time since the last meaningful movement before warning. Default is 2 hours.
                  </p>
                </div>
                <div>
                  <label htmlFor="staleTripAckWindow">Acknowledgement window (minutes)</label>
                  <input
                    id="staleTripAckWindow"
                    type="number"
                    step="1"
                    min="1"
                    max="60"
                    value={form.staleTripSettings?.acknowledgementMinutes ?? ''}
                    onChange={(e) => setForm((p) => ({ ...p, staleTripSettings: { ...p.staleTripSettings, acknowledgementMinutes: e.target.value } }))}
                    disabled={!form.staleTripSettings?.enabled}
                  />
                  <p className="hint">
                    How long the driver has to tap “Yes, I’m Active” before cancellation. Default is 5 minutes.
                  </p>
                </div>
                <div>
                  <label htmlFor="staleMovement">Meaningful movement (meters)</label>
                  <input id="staleMovement" type="number" min="10" max="5000" step="10"
                    value={form.staleTripSettings?.meaningfulMovementMeters ?? ''}
                    onChange={(e) => setForm((p) => ({ ...p, staleTripSettings: { ...p.staleTripSettings, meaningfulMovementMeters: e.target.value } }))}
                    disabled={!form.staleTripSettings?.enabled} />
                  <p className="hint">Distance required to reset the inactivity clock. Default is 100 meters.</p>
                </div>
              </div>
              </SettingsCard>

              <SettingsCard
                id="live-tracking"
                category="Driver app"
                title="Live tracking"
                description="Telemetry frequency, GPS quality and raw-location retention."
                open={openSections.has('live-tracking')}
                onToggle={() => toggleSection('live-tracking')}
              >
              <div className="form-grid">
                {[['movingIntervalSeconds', 'Moving interval (seconds)', 3, 60], ['stationaryIntervalSeconds', 'Stationary interval (seconds)', 10, 300], ['maxAcceptedAccuracyMeters', 'Maximum GPS accuracy (meters)', 5, 250], ['rawTelemetryRetentionDays', 'Raw telemetry retention (days)', 1, 30]].map(([key, label, min, max]) => <div key={key}><label htmlFor={`tracking-${key}`}>{label}</label><input id={`tracking-${key}`} type="number" min={min} max={max} value={form.trackingSettings?.[key] ?? ''} onChange={(e) => setForm((p) => ({ ...p, trackingSettings: { ...p.trackingSettings, [key]: e.target.value } }))} />{key === 'rawTelemetryRetentionDays' ? <p className="hint">Raw GPS points expire after processing. Compact safety history and scores are retained separately.</p> : null}</div>)}
              </div>
              </SettingsCard>

              <SettingsCard
                id="mobile-updates"
                category="Driver app"
                title="Required mobile app updates"
                description="Minimum Android and iPhone versions, store links and messages."
                open={openSections.has('mobile-updates')}
                onToggle={() => toggleSection('mobile-updates')}
              >
              <label style={{ display: 'inline-flex', gap: 10, alignItems: 'center', marginBottom: 18 }}>
                <input
                  type="checkbox"
                  checked={Boolean(form.mobileUpdateSettings?.enabled)}
                  onChange={(e) => setForm((p) => ({ ...p, mobileUpdateSettings: { ...p.mobileUpdateSettings, enabled: e.target.checked } }))}
                />
                <strong>Enforce minimum app versions</strong>
              </label>
              {['android', 'ios'].map((platform) => {
                const label = platform === 'android' ? 'Android / Google Play' : 'iPhone / App Store';
                const policy = form.mobileUpdateSettings?.[platform] || {};
                const updatePlatform = (field, value) => setForm((previous) => ({
                  ...previous,
                  mobileUpdateSettings: {
                    ...previous.mobileUpdateSettings,
                    [platform]: { ...previous.mobileUpdateSettings?.[platform], [field]: value },
                  },
                }));
                return (
                  <div key={platform} style={{ padding: '16px 0', borderTop: '1px solid var(--border-color)' }}>
                    <h5 style={{ margin: '0 0 12px' }}>{label}</h5>
                    <div className="form-grid">
                      <div>
                        <label htmlFor={`${platform}-minimum-version`}>Minimum version</label>
                        <input id={`${platform}-minimum-version`} type="text" inputMode="numeric" placeholder="2.0.0" value={policy.minimumVersion ?? ''} onChange={(e) => updatePlatform('minimumVersion', e.target.value)} disabled={!form.mobileUpdateSettings?.enabled} />
                      </div>
                      <div>
                        <label htmlFor={`${platform}-minimum-build`}>Minimum build number</label>
                        <input id={`${platform}-minimum-build`} type="number" min="0" step="1" value={policy.minimumBuildNumber ?? ''} onChange={(e) => updatePlatform('minimumBuildNumber', e.target.value)} disabled={!form.mobileUpdateSettings?.enabled} />
                      </div>
                      <div style={{ gridColumn: '1 / -1' }}>
                        <label htmlFor={`${platform}-store-url`}>Store URL</label>
                        <input id={`${platform}-store-url`} type="url" value={policy.storeUrl ?? ''} onChange={(e) => updatePlatform('storeUrl', e.target.value)} disabled={!form.mobileUpdateSettings?.enabled} />
                      </div>
                      <div style={{ gridColumn: '1 / -1' }}>
                        <label htmlFor={`${platform}-update-message`}>Driver message</label>
                        <textarea id={`${platform}-update-message`} rows="2" maxLength="500" value={policy.message ?? ''} onChange={(e) => updatePlatform('message', e.target.value)} disabled={!form.mobileUpdateSettings?.enabled} />
                      </div>
                    </div>
                  </div>
                );
              })}
              <p className="hint">Version and build are both enforced. The app is blocked if either installed value is below the saved minimum.</p>
              </SettingsCard>

              <SettingsCard
                id="safety-scoring"
                category="Safety"
                title="Driver safety scoring"
                description="Scoring sensitivity, minimum mileage and advanced thresholds."
                open={openSections.has('safety-scoring')}
                onToggle={() => toggleSection('safety-scoring')}
              >
              <label style={{ display: 'inline-flex', gap: 10, alignItems: 'center', marginBottom: 14 }}><input type="checkbox" checked={Boolean(form.safetyScoringSettings?.enabled)} onChange={(e) => setForm((p) => ({ ...p, safetyScoringSettings: { ...p.safetyScoringSettings, enabled: e.target.checked } }))} /><strong>Enable safety scoring</strong></label>
              <div className="form-grid">
                <div><label htmlFor="safetyPreset">Sensitivity</label><select id="safetyPreset" value={form.safetyScoringSettings?.preset || 'balanced'} onChange={(e) => setForm((p) => ({ ...p, safetyScoringSettings: { ...p.safetyScoringSettings, preset: e.target.value } }))}><option value="strict">Strict</option><option value="balanced">Balanced</option><option value="tolerant">Tolerant</option><option value="custom">Custom / advanced</option></select></div>
                <div><label htmlFor="minimumMileage">Minimum qualifying mileage</label><input id="minimumMileage" type="number" min="1" max="1000" value={form.safetyScoringSettings?.minimumMileage ?? ''} onChange={(e) => setForm((p) => ({ ...p, safetyScoringSettings: { ...p.safetyScoringSettings, minimumMileage: e.target.value } }))} /><p className="hint">A live estimate appears immediately; this mileage is required before it becomes a qualified score for fleet and insurance calculations.</p></div>
                <div><label htmlFor="eventRateBasisMiles">Event-rate mileage basis</label><input id="eventRateBasisMiles" type="number" min="1" max="10000" step="1" value={form.safetyScoringSettings?.eventRateBasisMiles ?? ''} onChange={(e) => setForm((p) => ({ ...p, safetyScoringSettings: { ...p.safetyScoringSettings, eventRateBasisMiles: e.target.value } }))} /><p className="hint">Display road events per this many valid miles, such as 100, 500, or 1,000.</p></div>
              </div>
              {form.safetyScoringSettings?.preset === 'custom' ? <div className="form-grid" style={{ marginTop: 14 }}>
                {[['accelerationThresholdMps2', 'Acceleration (m/s²)'], ['brakingThresholdMps2', 'Braking (negative m/s²)'], ['corneringThresholdMps2', 'Cornering (m/s²)'], ['speedingOverMph', 'Speeding over limit (mph)'], ['severeSpeedingOverMph', 'Severe speeding (mph)'], ['speedingMinimumSeconds', 'Speeding minimum (seconds)'], ['maxGpsAccuracyMeters', 'GPS accuracy max (meters)'], ['eventCooldownSeconds', 'Event cooldown (seconds)']].map(([key, label]) => <div key={key}><label htmlFor={`safety-${key}`}>{label}</label><input id={`safety-${key}`} type="number" step="0.1" value={form.safetyScoringSettings?.[key] ?? ''} onChange={(e) => setForm((p) => ({ ...p, safetyScoringSettings: { ...p.safetyScoringSettings, [key]: e.target.value } }))} /></div>)}
                {Object.entries({ acceleration: 'Acceleration weight', braking: 'Braking weight', cornering: 'Cornering weight', moderateSpeedingPerMinute: 'Moderate speeding / minute', severeSpeedingPerMinute: 'Severe speeding / minute' }).map(([key, label]) => <div key={key}><label htmlFor={`weight-${key}`}>{label}</label><input id={`weight-${key}`} type="number" min="0" max="20" step="0.1" value={form.safetyScoringSettings?.weights?.[key] ?? ''} onChange={(e) => setForm((p) => ({ ...p, safetyScoringSettings: { ...p.safetyScoringSettings, weights: { ...p.safetyScoringSettings.weights, [key]: e.target.value } } }))} /></div>)}
              </div> : null}
              </SettingsCard>

              <SettingsCard
                id="safety-report"
                category="Safety"
                title="Safety email report"
                description="Schedule recurring fleet-safety reports and choose their timezone."
                open={openSections.has('safety-report')}
                onToggle={() => toggleSection('safety-report')}
              >
              <label style={{ display: 'inline-flex', gap: 10, alignItems: 'center', marginBottom: 14 }}><input type="checkbox" checked={Boolean(form.safetyDigestSettings?.enabled)} onChange={(e) => setForm((p) => ({ ...p, safetyDigestSettings: { ...p.safetyDigestSettings, enabled: e.target.checked } }))} /><strong>Send scheduled safety report</strong></label>
              <div className="form-grid">
                <div><label htmlFor="digestFrequency">Frequency</label><select id="digestFrequency" value={form.safetyDigestSettings?.frequency || 'weekly'} onChange={(e) => setForm((p) => ({ ...p, safetyDigestSettings: { ...p.safetyDigestSettings, frequency: e.target.value } }))}><option value="weekly">Weekly</option><option value="biweekly">Biweekly</option><option value="monthly">Monthly</option></select></div>
                {form.safetyDigestSettings?.frequency === 'monthly' ? <div><label htmlFor="digestDay">Day of month</label><input id="digestDay" type="number" min="1" max="28" value={form.safetyDigestSettings?.dayOfMonth ?? 1} onChange={(e) => setForm((p) => ({ ...p, safetyDigestSettings: { ...p.safetyDigestSettings, dayOfMonth: e.target.value } }))} /></div> : <div><label htmlFor="digestWeekday">Weekday</label><select id="digestWeekday" value={form.safetyDigestSettings?.weekday ?? 1} onChange={(e) => setForm((p) => ({ ...p, safetyDigestSettings: { ...p.safetyDigestSettings, weekday: e.target.value } }))}>{['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((day, index) => <option key={day} value={index}>{day}</option>)}</select></div>}
                <div><label htmlFor="digestTime">Local send time</label><input id="digestTime" type="time" value={form.safetyDigestSettings?.localTime || '07:00'} onChange={(e) => setForm((p) => ({ ...p, safetyDigestSettings: { ...p.safetyDigestSettings, localTime: e.target.value } }))} /></div>
                <div><label htmlFor="digestTimezone">Timezone</label><input id="digestTimezone" type="text" value={form.safetyDigestSettings?.timezone || 'America/New_York'} onChange={(e) => setForm((p) => ({ ...p, companyTimezone: e.target.value, safetyDigestSettings: { ...p.safetyDigestSettings, timezone: e.target.value } }))} /></div>
              </div>
              </SettingsCard>
            </div>

            {error && <div className="feedback error">{error}</div>}
            {success && <div className="feedback success">{success}</div>}

            <div className="panel-footer" style={{ justifyContent: 'flex-end' }}>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Saving...' : 'Save changes'}
              </button>
            </div>
          </form>
        )}
      </div>
    </AppLayout>
  );
};

export default CompanySettings;
